import type { HsDisposable } from './disposable.interface';
import type { HsFormatDecoder } from './format-decoder.interface';

export function hsNormalizeContentType(contentType: string): string {
  return contentType.split(';')[0].trim().toLowerCase();
}

/** Maps media types to decoders. A later registration for the same type replaces the earlier one until disposed. */
export class HsFormatRegistry {
  private readonly decoders: Map<string, HsFormatDecoder[]> = new Map();

  public register(decoder: HsFormatDecoder): HsDisposable {
    const contentTypes: string[] = decoder.contentTypes.map(hsNormalizeContentType);
    for (const contentType of contentTypes) {
      const stack: HsFormatDecoder[] = this.decoders.get(contentType) ?? [];
      stack.push(decoder);
      this.decoders.set(contentType, stack);
    }
    let disposed: boolean = false;
    return {
      dispose: (): void => {
        if (disposed) {
          return;
        }
        disposed = true;
        for (const contentType of contentTypes) {
          const stack: HsFormatDecoder[] | undefined = this.decoders.get(contentType);
          const index: number = stack?.lastIndexOf(decoder) ?? -1;
          if (stack !== undefined && index >= 0) {
            stack.splice(index, 1);
            if (stack.length === 0) {
              this.decoders.delete(contentType);
            }
          }
        }
      },
    };
  }

  public find<T = unknown>(contentType: string): HsFormatDecoder<T> | null {
    const stack: HsFormatDecoder[] | undefined = this.decoders.get(hsNormalizeContentType(contentType));
    return (stack?.[stack.length - 1] as HsFormatDecoder<T> | undefined) ?? null;
  }

  /** Registered media types among `candidates`, keeping the caller's preference order; feeds `Accept`. */
  public accepted(candidates: readonly string[]): string[] {
    return candidates.filter((contentType: string) => this.decoders.has(hsNormalizeContentType(contentType)));
  }
}
